import React from 'react';
import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Dog,
  X,
  BarChart,
  Settings,
  Clock,
  ClipboardList,
  DollarSign,
  Activity,
  Home
} from 'lucide-react';
import logo from '../assets/pawley-club-logo.png';

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, toggleSidebar }) => {
  const dashboardItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/', color: 'blue' },
  ];

  const kopekMenuItems = [
    { icon: Dog, label: 'Oteldeki Kedi/Köpekler', path: '/otel', color: 'orange' },
    { icon: Activity, label: 'Durum Güncellemeleri', path: '/durum', color: 'orange' },
    { icon: Clock, label: 'Geçmiş Ziyaretler', path: '/gecmis', color: 'orange' },
  ];

  const personelItems = [
    { icon: Users, label: 'Personel Listesi', path: '/personel', color: 'indigo' },
  ];

  const raporItems = [
    { icon: DollarSign, label: 'Aylık Ödeme Raporu', path: '/odeme', color: 'yellow' },
    { icon: ClipboardList, label: 'Anket Sonuçları', path: '/anket', color: 'yellow' },
  ];

  const ayarItems = [
    { icon: Settings, label: 'Hesap Ayarları', path: '/ayarlar', color: 'gray' },
    { icon: Home, label: 'Şube Bilgileri', path: '/sube', color: 'gray' },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full w-72 bg-gradient-to-b from-slate-800 via-gray-900 to-slate-900 shadow-2xl transform transition-all duration-300 ease-in-out z-30 border-r border-slate-700
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:z-auto
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-md p-2 ring-1 ring-white/20">
              <img src={logo} alt="PawleyClub Logo" className="w-8 h-8 rounded-lg object-cover" />
            </div>
            <div>
              <h1 className="text-lg font-black text-white">PawleyClub</h1>
              <p className="text-gray-300 text-xs font-medium">Admin Panel</p>
            </div>
          </div>
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2 rounded-xl hover:bg-white/10 text-gray-300 hover:text-white transition-all duration-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-5 overflow-y-auto max-h-[calc(100vh-200px)] px-4 sidebar-scroll">
          {/* Dashboard */}
          <div className="mb-5">
            <ul className="space-y-2">
              {dashboardItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-3 rounded-xl transition-all duration-200 group ${
                        isActive
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                          : 'text-gray-300 hover:bg-white/10 hover:text-white'
                      }`
                    }
                  >
                    <item.icon size={20} className="mr-3 group-hover:scale-110 transition-transform duration-200" />
                    <span className="font-bold">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Köpek Yönetimi */}
          <div className="mb-5">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5 px-3 flex items-center">
              <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mr-2"></div>
              Köpek Yönetimi
            </h3>
            <ul className="space-y-1.5">
              {kopekMenuItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                    className={({ isActive }) =>
                      `flex items-center px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                        isActive
                          ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-md'
                          : 'text-gray-300 hover:bg-white/10 hover:text-white'
                      }`
                    }
                  >
                    <item.icon size={18} className="mr-3 group-hover:scale-110 transition-transform duration-200" />
                    <span className="font-semibold text-sm">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Personel */}
          <div className="mb-5">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5 px-3 flex items-center">
              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2"></div>
              Personel
            </h3>
            <ul className="space-y-1.5">
              {personelItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                    className={({ isActive }) =>
                      `flex items-center px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                        isActive
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md'
                          : 'text-gray-300 hover:bg-white/10 hover:text-white'
                      }`
                    }
                  >
                    <item.icon size={18} className="mr-3 group-hover:scale-110 transition-transform duration-200" />
                    <span className="font-semibold text-sm">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Raporlar */}
          <div className="mb-5">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5 px-3 flex items-center">
              <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-2"></div>
              Raporlar
            </h3>
            <ul className="space-y-1.5">
              {raporItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                    className={({ isActive }) =>
                      `flex items-center px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                        isActive
                          ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md'
                          : 'text-gray-300 hover:bg-white/10 hover:text-white'
                      }`
                    }
                  >
                    <item.icon size={18} className="mr-3 group-hover:scale-110 transition-transform duration-200" />
                    <span className="font-semibold text-sm">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Ayarlar */}
          <div className="mb-5">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5 px-3 flex items-center">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2"></div>
              Ayarlar
            </h3>
            <ul className="space-y-1.5">
              {ayarItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                    className={({ isActive }) =>
                      `flex items-center px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                        isActive
                          ? 'bg-gradient-to-r from-slate-500 to-gray-600 text-white shadow-md'
                          : 'text-gray-300 hover:bg-white/10 hover:text-white'
                      }`
                    }
                  >
                    <item.icon size={18} className="mr-3 group-hover:scale-110 transition-transform duration-200" />
                    <span className="font-semibold text-sm">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Footer */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="bg-gradient-to-r from-slate-700 to-gray-800 backdrop-blur-sm rounded-xl p-4 border border-slate-600 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-white">Admin Panel</p>
                <p className="text-xs text-gray-300 font-medium">v3.0 • PawleyClub</p>
              </div>
              <div className="flex items-center space-x-1.5">
                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-sm"></div>
                <span className="text-xs text-emerald-400 font-bold">Aktif</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
